/**
 * What the student called before the tool reported, in the debrief.
 *
 * The technicians collect three kinds of thing about a student now - what
 * they asked, what they were shown, and what they CALLED - and only the last
 * of those is a record of what they believed. It was read in exactly one
 * place: the brief of the technician standing at the tool where it happened.
 * Nobody else ever saw it, the student who made the call least of all.
 *
 * So it goes where a student actually stops and reads. Both debriefs take it:
 * the work-order modal at the end of an order, and the shift receipt inside a
 * tool panel.
 *
 * WITHOUT A VERDICT, like everything else on these screens. The call and the
 * measurement are laid out one above the other and the student compares them
 * - which is exactly what the technician does out loud, and exactly what
 * nothing in the code is allowed to do. See the `prediction` case in
 * learn/Journal.ts, and TALK.md's "Calling it first".
 */

import type { Call } from '../../learn/shift'
import { t } from '../../i18n/i18n'

export function CallsMade({ calls }: { calls: Call[] }) {
  if (!calls.length) return null
  return (
    <div className="calls">
      <div className="blk">
        <div className="k">{t('shift.CallsHeading')}</div>
      </div>
      <div className="calls-note">{t('shift.CallsNote')}</div>
      {calls.map((call, i) => (
        <div className="call" key={i}>
          <div className="call-about">{call.about}</div>
          {/* Their words first and the tool's second, in the order they
              happened: the student reads their own sentence, then finds out. */}
          <div className="call-said">
            <span className="call-who">{t('shift.CallsSaid')}</span> {call.said}
          </div>
          <div className="call-measured">
            <span className="call-who">{t('shift.CallsMeasured')}</span> {call.measured}
          </div>
          {/* And what they made of it, if she asked. Labelled "when asked why"
              rather than "you explained it as": a student who changed the
              subject still said something, and the record keeps the utterance
              without judging whether it answered. */}
          {call.explained && (
            <div className="call-explained">
              <span className="call-who">{t('shift.CallsExplained')}</span> {call.explained}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
